import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import * as ROUTES from '../constants/routes';
import { useCelebrityByRating } from '../hooks';
import { setCurrentField } from '../actions';
import { getCurrentField, getFields } from '../reducers';
import CelebrityCard from './Ratings/CelebrityCard';
import { blogService } from '../services/blogService';
import { translateFieldLabel } from '../utils/fields';

export default function Home() {
  const fields = useSelector(getFields);
  const field = useSelector(getCurrentField);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const { celebrities, isFetching, error, orderBy } = useCelebrityByRating(
    field
  );

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const posts = await blogService.getLatestPosts(2);
        setBlogPosts(posts);
      } catch (error) {
        console.error(' Error loading blog posts:', error);
      } finally {
        setLoadingBlogs(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // AdSense durumunu kontrol et ve main element'in class'ını ayarla
  useEffect(() => {
    const checkAdSenseStatus = () => {
      const adsenseApproved = localStorage.getItem('adsenseApproved') === 'true';
      const mainElement = document.querySelector('main');
      
      if (mainElement) {
        if (adsenseApproved) {
          mainElement.classList.remove('no-adsense');
        } else {
          mainElement.classList.add('no-adsense');
        }
      }
    };

    checkAdSenseStatus();
    
    // Sayfa yüklendiğinde tekrar kontrol et
    const timer = setTimeout(checkAdSenseStatus, 100);
    
    return () => clearTimeout(timer);
  }, []);

  function handleFieldChange(event) {
    dispatch(setCurrentField(event.target.value));
  }

  function renderCelebrities(celebrities) {
    return celebrities.map((celebrity, idx) => (
      <CelebrityCard
        key={celebrity.id}
        index={idx}
        celebrity={celebrity}
        text={t('ratings.ratingLabel', {
          value: Math.round(celebrity[orderBy]) || '-',
        })}
      />
    ));
  }

  return (
    <div className="container">
      <div className="jumbotron jumbotron-fluid mb-3 mt-3">
        <div className="container text-center">
          <h1 className="display-4" style={{ color: '#0A2342', fontWeight: 'bold' }}>{t('home.heroTitle')}</h1>
          <p
            className="lead"
            style={{ fontSize: '2rem', color: '#0A2342' }}
          >
            {t('home.heroSubtitle')}
          </p>
          <Link 
          className="btn btn-primary" 
          style={{   color: "white" }}
          to={ROUTES.VOTE}>
            {t('home.heroCta')}
          </Link>
        </div>
      </div>
      <div className="jumbotron jumbotron-fluid mb-3">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 d-flex flex-column align-items-center justify-content-center text-center pb-4 pb-xl-0">
              <h1
                style={{ fontSize: '2.5rem', color: '#0A2342', fontWeight: 'bold' }}
              >
                {t('home.ratingsTitle')}
              </h1>
              <p
                className="lead"
                style={{ fontSize: '2rem', color: '#0A2342' }}
              >
                {t('home.ratingsSubtitle')}
              </p>
              <Link
                className="btn btn-primary "
                style={{   color: "white" }}
                to={ROUTES.RATINGS}
              >
                {t('home.ratingsCta')}
              </Link>
            </div>
            <div className="col-lg-6">
              <div className="row mb-2">
                <div className="col">
                  <h3 style={{ color: '#0A2342', fontWeight: 'bold' }}>{t('home.currentRatings')}</h3>
                </div>
                <div className="col-auto d-flex justify-content-end">
                  <form className="form-inline">
                    <select
                      className="form-select"
                      name="field"
                      onChange={handleFieldChange}
                      value={field}
                    >
                      {Object.keys(fields)
                        .map(key => ({
                          key,
                          label: translateFieldLabel(fields, key, t),
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map(({ key, label }) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                    </select>
                  </form>
                </div>
              </div>
              {!isFetching && !celebrities.length ? (
                <div className="row">
                  <div className="col-12 mb-2 text-center">
                    <span style={{ color: '#0A2342' }}>
                      {t('home.emptyState')}
                    </span>
                  </div>
                </div>
              ) : (
                renderCelebrities(celebrities)
              )}
              {isFetching ? (
                <div className="row">
                  <div className="col-12 mb-2 text-center">
                    <span
                      className="spinner-grow spinner-grow-sm text-secodary"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="sr-only">{t('common.loading')}</span>
                  </div>
                </div>
              ) : (
                error && (
                  <div className="row">
                    <div className="col-12 mb-2 text-center">
                      <span className="text-danger">{error.message}</span>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      
      
      
      {/* Son Blog Yazıları Bölümü */}
      <div className="jumbotron jumbotron-fluid mb-3">
        <div className="container">
          <div className="row mb-4">
            <div className="col">
              <h2 style={{ color: '#0A2342' }}>{t('home.latestBlogPosts')}</h2>
            </div>
            <div className="col-auto">
              <Link to={ROUTES.BLOG} className="btn btn-outline-primary">
                {t('common.viewAll')}
              </Link>
            </div>
          </div>
          
          {loadingBlogs ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">{t('common.loading')}</span>
              </div>
            </div>
          ) : blogPosts.length > 0 ? (
            <div className="row">
              {blogPosts.map(post => (
                <div key={post.id} className="col-lg-6 col-md-12 mb-3">
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          {post.createdAt ? moment(post.createdAt.toDate()).format('DD.MM.YYYY') : t('common.noDate')}
                        </small>
                        {post.category && (
                          <span className="badge badge-primary">{post.category}</span>
                        )}
                      </div>
                      <h3 className="h5 mb-2">
                        <Link to={`${ROUTES.BLOG}/${post.id}`} style={{ color: '#0A2342', textDecoration: 'none' }}>
                          {post.title}
                        </Link>
                      </h3>
                      <div className="mb-2" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                      <div className="d-flex justify-content-end">
                                                  <Link 
                            to={`${ROUTES.BLOG}/${post.id}`} 
                            className="btn btn-primary btn-sm"
                            onMouseEnter={(e) => { e.target.style.color = '#FFFFFF'; }}
                            onMouseLeave={(e) => { e.target.style.color = '#F5F5D1'; }}
                            style={{ color: '#F5F5D1' }}
                          >
                            {t('common.readMore')}
                          </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info">
              {t('home.noPosts')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
